// The event write path's function references (bead hf-e4-events-needs-6r5.1).
//
// Everything the browser knows about the backend comes through
// `convex/_generated/api` — never a Convex module directly, which dependency-cruiser
// enforces (`app-uses-generated-convex-api-only`). Importing a domain module would
// mean the client was talking to handler code with no capability check, which is
// R-048 in its purest form.
//
// Return types are INFERRED from the generated types rather than restated, so the
// browser cannot drift from what the functions actually return.

import type { FunctionReturnType } from "convex/server";
import { ERROR_MESSAGES, LIFECYCLE_REJECTION_MESSAGES } from "@herefor/product-language";

import { api } from "../../../../convex/_generated/api";

export const eventQueries = {
  get: api.domain.events.getEvent,
  list: api.domain.events.listEvents,
  revisions: api.domain.events.listEventRevisions,
} as const;

export const eventMutations = {
  create: api.domain.events.createEvent,
  update: api.domain.events.updateEvent,
  publish: api.domain.events.publishEvent,
  complete: api.domain.events.completeEvent,
  cancel: api.domain.events.cancelEvent,
  deleteDraft: api.domain.events.deleteDraftEvent,
} as const;

export type GetEventResult = FunctionReturnType<typeof eventQueries.get>;
export type ListEventsResult = FunctionReturnType<typeof eventQueries.list>;
export type ListEventRevisionsResult = FunctionReturnType<typeof eventQueries.revisions>;
export type EventWriteOutcome = FunctionReturnType<typeof eventMutations.update>;

/** Translate a wrapped domain lifecycle refusal into reviewed copy. */
export function lifecycleProblem(outcome: unknown): string | null {
  if (typeof outcome !== "object" || outcome === null || !("status" in outcome)) {
    return ERROR_MESSAGES.itemUnavailable.body;
  }
  const wrapped = outcome as { readonly status: string; readonly value?: unknown };
  if (wrapped.status === "replayed") return null;
  if (wrapped.status !== "applied") return ERROR_MESSAGES.itemUnavailable.body;
  if (typeof wrapped.value !== "object" || wrapped.value === null || !("ok" in wrapped.value)) {
    return ERROR_MESSAGES.itemUnavailable.body;
  }
  const result = wrapped.value as { readonly ok: boolean; readonly rejection?: unknown };
  if (result.ok) return null;
  if (
    typeof result.rejection === "object" &&
    result.rejection !== null &&
    "kind" in result.rejection &&
    result.rejection.kind === "illegal_transition" &&
    "reason" in result.rejection
  ) {
    const reason = result.rejection.reason;
    if (typeof reason === "string" && reason in LIFECYCLE_REJECTION_MESSAGES) {
      return LIFECYCLE_REJECTION_MESSAGES[reason as keyof typeof LIFECYCLE_REJECTION_MESSAGES];
    }
  }
  return ERROR_MESSAGES.itemUnavailable.body;
}
